/**
 * useToast Hook
 * 
 * Hook for accessing toast notification functionality.
 * Must be used within a ToastProvider.
 * 
 * @example
 * const { showSuccess, showError } = useToast();
 * showSuccess('פעולה בוצעה בהצלחה');
 */

import { useContext, createContext } from 'react';

// =============================================================================
// Types
// =============================================================================

type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  dismissToast: (id: string) => void;
  dismissAll: () => void;
}

// =============================================================================
// Context
// =============================================================================

export const ToastContext = createContext<ToastContextType | null>(null);

// =============================================================================
// Hook
// =============================================================================

export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
